import { ActiveSelection, type Canvas, type FabricObject } from 'fabric';

// Library item an image layer was dropped from; saved so the canvas references templates/SKUs by id.
export interface LayerSourceRef {
  kind: 'template' | 'sku';
  id: string | number;
}

export type LayerObject = FabricObject & {
  layerName?: string;
  layerId?: number;
  isGridLine?: boolean;
  isSafeZone?: boolean;
  sourceRef?: LayerSourceRef;
};

const MAX_NAME_PREVIEW_LENGTH = 24;

// Drag payload type carrying the name an element should get as a layer when dropped on the canvas.
export const LAYER_NAME_DRAG_TYPE = 'application/x-canvas-layer-name';
// Drag payload type carrying the LayerSourceRef (as JSON) of the dropped library item.
export const LAYER_SOURCE_DRAG_TYPE = 'application/x-canvas-source';

export const isTemplateLayer = (object: LayerObject): boolean => object.sourceRef?.kind === 'template';

export const parseLayerSourceRef = (value: string): LayerSourceRef | undefined => {
  try {
    const parsed = JSON.parse(value) as Partial<LayerSourceRef>;
    if ((parsed.kind === 'template' || parsed.kind === 'sku') && (typeof parsed.id === 'string' || typeof parsed.id === 'number')) {
      return { kind: parsed.kind, id: parsed.id };
    }
  } catch {
    // Not JSON: the drag didn't come from the library panels.
  }
  return undefined;
};

// User objects (excludes the grid/safe zone helper lines), in the same
// order fabric uses internally: index 0 = furthest back, last = frontmost.
export const getLayerObjects = (canvas: Canvas): LayerObject[] =>
  (canvas.getObjects() as LayerObject[]).filter((object) => !object.isGridLine && !object.isSafeZone);

// Same check as isTextObject() in CanvasFloatingToolbar.tsx: IText is the only type
// that has 'fontFamily', so it tells text from image regardless of their classes.
const isTextLayer = (object: LayerObject) => 'fontFamily' in object;

export const getLayerType = (object: LayerObject): 'text' | 'image' => (isTextLayer(object) ? 'text' : 'image');

// The layer name never touches the actual text content: uses layerName if the user
// already renamed the layer; otherwise builds a descriptive name for display in the panel only.
export const getLayerName = (object: LayerObject): string => {
  if (object.layerName) return object.layerName;
  if (isTextLayer(object)) {
    const text = String((object as unknown as { text?: string }).text ?? '');
    return text.length > MAX_NAME_PREVIEW_LENGTH ? `${text.slice(0, MAX_NAME_PREVIEW_LENGTH)}…` : text || 'Texto';
  }
  return 'Imagen';
};

let nextLayerId = 1;

// Stable id per object (lazily assigned) to use as a React key
// across reorders, since fabric provides no unique identifier by default.
export const getLayerId = (object: LayerObject): number => {
  if (!object.layerId) object.layerId = nextLayerId++;
  return object.layerId;
};

export const isLayerLocked = (object: LayerObject): boolean => !!object.lockMovementX;

// Locks position, size and rotation (hiding the resize/rotate handles) and, for text, editing its content.
// lockMovementX is the flag the lock is read from; the rest follow it.
export const setLayerLocked = (object: LayerObject, locked: boolean) => {
  object.set({
    lockMovementX: locked,
    lockMovementY: locked,
    lockScalingX: locked,
    lockScalingY: locked,
    lockRotation: locked,
    lockSkewingX: locked,
    lockSkewingY: locked,
    hasControls: !locked,
  });
  if ('editable' in object) {
    object.set({ editable: !locked });
    // A text being typed into when it gets locked stops editing.
    const text = object as LayerObject & { isEditing?: boolean; exitEditing?: () => void };
    if (locked && text.isEditing) text.exitEditing?.();
  }
};

// What the lock controls (the context menu, the toolbar) act on: the selected element, or every element of a
// multi-selection, which counts as locked when all of them are. The selection itself follows, so it can't be
// moved either.
export const isTargetLocked = (target: FabricObject): boolean =>
  target instanceof ActiveSelection ? target.getObjects().every(isLayerLocked) : isLayerLocked(target);

export const setTargetLocked = (target: FabricObject, locked: boolean) => {
  if (target instanceof ActiveSelection) target.getObjects().forEach((object) => setLayerLocked(object, locked));
  setLayerLocked(target, locked);
};

// A multi-selection moves and transforms all its members together, ignoring their own locks. Locked
// elements are dropped from it, so a marquee over a locked background still grabs the rest; when every
// member is locked, the selection itself is locked. Returns true when it replaced the active selection.
export const enforceSelectionLocks = (canvas: Canvas): boolean => {
  const selection = canvas.getActiveObject();
  if (!(selection instanceof ActiveSelection)) return false;
  const members = selection.getObjects();
  const unlocked = members.filter((object) => !isLayerLocked(object));
  if (unlocked.length === members.length) return false;
  if (unlocked.length === 0) {
    setLayerLocked(selection, true);
    return false;
  }
  canvas.setActiveObject(unlocked.length === 1 ? unlocked[0] : new ActiveSelection(unlocked, { canvas }));
  canvas.requestRenderAll();
  return true;
};

export const setLayerHidden = (object: LayerObject, hidden: boolean) => {
  object.set({ visible: !hidden });
};

// The guides are drawn above every element so they stay visible: the grid, and the safe zone over it.
// Fabric stacks a new object on top, so this runs again whenever something is added.
export const keepGuidesOnTop = (canvas: Canvas) => {
  const objects = canvas.getObjects() as LayerObject[];
  objects.filter((o) => o.isGridLine).forEach((line) => canvas.bringObjectToFront(line));
  objects.filter((o) => o.isSafeZone).forEach((line) => canvas.bringObjectToFront(line));
};

// Reorders the user objects according to the given array (back to front), keeping
// the grid/safe zone on top.
export const reorderLayers = (canvas: Canvas, orderedUserObjects: LayerObject[]) => {
  orderedUserObjects.forEach((object, index) => canvas.moveObjectTo(object, index));
  keepGuidesOnTop(canvas);
  canvas.requestRenderAll();
};

// Applies the new order as shown in the panel (front to back); it is reversed
// once here so the panel doesn't have to reason about fabric's internal z-index.
export const reorderLayersFromDisplayOrder = (canvas: Canvas, newDisplayOrder: LayerObject[]) => {
  reorderLayers(canvas, [...newDisplayOrder].reverse());
};

export type LayerMoveDirection = 'front' | 'forward' | 'backward' | 'back';

// Moves a layer one step (forward/backward) or to the extreme (front/back) of the z-index.
export const moveLayer = (canvas: Canvas, object: LayerObject, direction: LayerMoveDirection) => {
  const userObjects = getLayerObjects(canvas);
  const currentIndex = userObjects.indexOf(object);
  if (currentIndex === -1) return;

  let targetIndex = currentIndex;
  if (direction === 'forward') targetIndex = currentIndex + 1;
  else if (direction === 'backward') targetIndex = currentIndex - 1;
  else if (direction === 'front') targetIndex = userObjects.length - 1;
  else if (direction === 'back') targetIndex = 0;
  targetIndex = Math.max(0, Math.min(userObjects.length - 1, targetIndex));
  if (targetIndex === currentIndex) return;

  userObjects.splice(currentIndex, 1);
  userObjects.splice(targetIndex, 0, object);
  reorderLayers(canvas, userObjects);
};

export type LayerAlignment = 'left' | 'center-h' | 'right' | 'top' | 'center-v' | 'bottom';

// Target position on one axis: flush to the start, the end, or centered in the free space.
const getAlignedPosition = (isStart: boolean, isEnd: boolean, freeSpace: number) => {
  if (isStart) return 0;
  if (isEnd) return freeSpace;
  return freeSpace / 2;
};

// Aligns the object relative to the canvas (not to other objects). Uses the absolute
// bounding box (not left/top) for the same reason as the grid snap: the origin may
// be 'center', so aligning left/top directly would move the center, not the real edge.
export const alignLayer = (canvas: Canvas, object: LayerObject, alignment: LayerAlignment) => {
  object.setCoords();
  const bounds = object.getBoundingRect();
  const canvasWidth = canvas.getWidth();
  const canvasHeight = canvas.getHeight();

  if (alignment === 'left' || alignment === 'center-h' || alignment === 'right') {
    const targetLeft = getAlignedPosition(alignment === 'left', alignment === 'right', canvasWidth - bounds.width);
    object.set({ left: (object.left ?? 0) + (targetLeft - bounds.left) });
  } else {
    const targetTop = getAlignedPosition(alignment === 'top', alignment === 'bottom', canvasHeight - bounds.height);
    object.set({ top: (object.top ?? 0) + (targetTop - bounds.top) });
  }

  object.setCoords();
  canvas.requestRenderAll();
};
